import React, { useEffect, useState } from 'react'
import { Lock, X, Eye, Send } from 'lucide-react'
import { Drawer } from '../ui'
import { SharedReportListItem } from '../../types/sharedReport'

interface Props {
  isOpen: boolean
  onClose: () => void
  item: SharedReportListItem | null // the saved report being shared (context)
  onPreview?: () => void
  onSubmit?: (payload: { title: string; message: string; recipients: string[]; expiryDays: number; delivery: 'email' | 'copy'; requirePassword: boolean; password?: string }) => void
}

type Expiry = 30 | 90 | 365

// Titles feed the email subject + history/viewer labels, so keep them short.
const TITLE_MAX = 50

// Pragmatic email check — mirrors what the backend will re-validate (Joi) on /reports/:id/share.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const isValidEmail = (e: string): boolean => EMAIL_RE.test(e)

const SaveShareDrawer: React.FC<Props> = ({ isOpen, onClose, item, onPreview, onSubmit }) => {
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [recipients, setRecipients] = useState<string[]>([])
  const [draftEmail, setDraftEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [expiry, setExpiry] = useState<Expiry>(90)
  const [delivery, setDelivery] = useState<'email' | 'copy'>('email')
  const [requirePassword, setRequirePassword] = useState(false)
  const [password, setPassword] = useState('')

  // Seed from the report being shared when the drawer opens.
  useEffect(() => {
    if (!isOpen) return
    setTitle((item?.title ?? '').slice(0, TITLE_MAX))
    setMessage('')
    setRecipients(item?.recipients.map((r) => r.email) ?? [])
    setDraftEmail('')
    setEmailError(null)
    setExpiry(90)
    setDelivery('email')
    setRequirePassword(false)
    setPassword('')
  }, [isOpen, item])

  // Split on comma/semicolon/whitespace so a pasted distro list works; validate + normalize each.
  const addEmails = (raw: string) => {
    const parts = raw.split(/[\s,;]+/).map((p) => p.trim().toLowerCase()).filter(Boolean)
    if (parts.length === 0) { setDraftEmail(''); return }
    const valid: string[] = []
    const invalid: string[] = []
    for (const p of parts) {
      if (!isValidEmail(p)) invalid.push(p)
      else if (!recipients.includes(p) && !valid.includes(p)) valid.push(p)
    }
    if (valid.length) setRecipients((r) => [...r, ...valid])
    if (invalid.length) {
      setEmailError(`Enter a valid email address — check: ${invalid.join(', ')}`)
      setDraftEmail(invalid.join(', ')) // keep the bad ones so the user can fix them
    } else {
      setEmailError(null)
      setDraftEmail('')
    }
  }
  const onKey = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if ((ev.key === 'Enter' || ev.key === ',' || ev.key === ';') && draftEmail.trim()) {
      ev.preventDefault()
      addEmails(draftEmail)
    } else if (ev.key === 'Backspace' && !draftEmail && recipients.length) {
      setRecipients((r) => r.slice(0, -1))
    }
  }

  const seg = (active: boolean) =>
    `flex-1 py-2 text-xs font-semibold cursor-pointer transition-colors ${active ? 'bg-indigo-50 text-indigo-700' : 'bg-white text-gray-500 hover:bg-gray-50'}`

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Save & Share Report" description={item?.reportTypeLabel} width="md">
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto space-y-4 pb-4">
          <div className="flex gap-2.5 items-start bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-xs text-indigo-800">
            <Lock className="h-4 w-4 flex-none mt-0.5" />
            <span>A snapshot is frozen now (data as of today). Recipients open a link — no login — and can download the PDF.</span>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold text-gray-700">Report title</label>
              <span className={`text-[11px] ${title.length >= TITLE_MAX ? 'text-amber-600' : 'text-gray-400'}`}>{title.length}/{TITLE_MAX}</span>
            </div>
            <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))} maxLength={TITLE_MAX}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Message to recipients</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
              placeholder="Add a note — shown in the email and above the report."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Recipients</label>
            <div className={`flex flex-wrap gap-1.5 border rounded-lg p-2 focus-within:ring-2 ${emailError ? 'border-rose-300 focus-within:ring-rose-400 focus-within:border-rose-400' : 'border-gray-300 focus-within:ring-blue-500 focus-within:border-blue-500'}`}>
              {recipients.map((e) => (
                <span key={e} className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 rounded-md pl-2.5 pr-1.5 py-1 text-xs font-semibold">
                  {e}
                  <button type="button" onClick={() => setRecipients((r) => r.filter((x) => x !== e))}
                    className="h-4 w-4 rounded-full bg-indigo-100 flex items-center justify-center hover:bg-indigo-200" aria-label={`Remove ${e}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input value={draftEmail} type="email"
                onChange={(e) => { setDraftEmail(e.target.value); if (emailError) setEmailError(null) }}
                onKeyDown={onKey} onBlur={() => draftEmail.trim() && addEmails(draftEmail)}
                aria-invalid={!!emailError} aria-label="Add recipient email"
                placeholder={recipients.length ? 'Add email…' : 'Add email + Enter…'}
                className="flex-1 min-w-[120px] text-sm outline-none px-1 py-1" />
            </div>
            {emailError
              ? <p className="text-[11.5px] text-rose-600 mt-1.5">{emailError}</p>
              : <p className="text-[11.5px] text-gray-400 mt-1.5">Add multiple at once — type or paste a comma-separated list. Saved recipient lists coming soon.</p>}
          </div>

          <div className="grid grid-cols-2 gap-3.5">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Link expires</label>
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                {([30, 90, 365] as Expiry[]).map((d) => (
                  <button key={d} type="button" onClick={() => setExpiry(d)} className={seg(expiry === d)}>
                    {d === 365 ? '1yr' : `${d}d`}
                  </button>
                ))}
              </div>
              <p className="text-[11.5px] text-gray-400 mt-1.5">Default from customer setting.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Delivery</label>
              <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                <button type="button" title="Email the report link to your recipients" onClick={() => setDelivery('email')} className={seg(delivery === 'email')}>Email link</button>
                <button type="button" title="Create the link and copy it to your clipboard — no email is sent" onClick={() => setDelivery('copy')} className={seg(delivery === 'copy')}>Copy only</button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
            <div>
              <div className="text-[13px] font-semibold text-gray-800 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-gray-400" /> Require a password</div>
              <div className="text-[11.5px] text-gray-500">Recipients enter a password to view. Off by default.</div>
            </div>
            <button type="button" role="switch" aria-checked={requirePassword} onClick={() => setRequirePassword((v) => !v)}
              className={`relative h-[22px] w-[38px] rounded-full transition-colors flex-none ${requirePassword ? 'bg-indigo-600' : 'bg-gray-300'}`}>
              <span className={`absolute top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow transition-all ${requirePassword ? 'left-[18px]' : 'left-0.5'}`} />
            </button>
          </div>
          {requirePassword && (
            <input type="text" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Set a password recipients must enter"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
          )}
        </div>

        <div className="flex gap-2.5 pt-3 border-t border-gray-100">
          <button type="button" onClick={onPreview}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
            <Eye className="h-3.5 w-3.5" /> Preview
          </button>
          <button type="button"
            disabled={(delivery === 'email' && recipients.length === 0) || !!emailError}
            onClick={() => {
              // Flush a typed-but-not-yet-added email, validating synchronously.
              let final = recipients
              const draft = draftEmail.trim()
              if (draft) {
                const parts = draft.split(/[\s,;]+/).map((p) => p.trim().toLowerCase()).filter(Boolean)
                const bad = parts.filter((p) => !isValidEmail(p))
                if (bad.length) { setEmailError(`Enter a valid email address — check: ${bad.join(', ')}`); return }
                final = Array.from(new Set([...recipients, ...parts]))
                setRecipients(final); setDraftEmail('')
              }
              if (delivery === 'email' && final.length === 0) return
              if (requirePassword && !password.trim()) { setEmailError('Set a password or turn off password protection.'); return }
              onSubmit?.({ title, message, recipients: final, expiryDays: expiry, delivery, requirePassword, password: requirePassword ? password : undefined })
              onClose()
            }}
            className="flex-[1.4] inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-gradient-to-b from-indigo-600 to-indigo-700 text-white rounded-lg hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed">
            <Send className="h-3.5 w-3.5" /> {delivery === 'email' ? 'Save & Send' : 'Save & Copy Link'}
          </button>
        </div>
      </div>
    </Drawer>
  )
}

export default SaveShareDrawer
